import { z } from "zod";
import { Operation } from "./provider.js";

/** One pg-boss queue per operation plus the pipeline steps (10). */
export const JobName = z.enum([...Operation.options, "analyse", "compose", "publish", "publish_site", "check_live", "pull_search_console", "metrics_pull", "expire_lots", "purge_media", "sweep_parked", "export_workspace", "expire_exports", "purge_workspaces",
  // Customers (23): reading one company and judging it. Not `outreach_judge`, which is the model call inside it —
  // the job fetches the pages first, and a company is judged whether or not a model ever reads anything.
  "judge_prospect",
  // One letter, handed to the customer's own mailbox. Idempotent on the letter's id (22 §sending).
  "send_letter",
  // The website Joogoos makes, uploaded whole to the customer's own Cloudflare account (28 §6). Not AI, not billed.
  "deploy_website"]);
export type JobName = z.infer<typeof JobName>;

/**
 * Take everything with you (ADR 0019), in numbers both sides use: how long a finished export can be downloaded, how
 * large one zip gets before the next part starts, when an export still open has stopped being worth waiting for, and
 * how many one workspace may ask for in a day.
 */
export const EXPORT = { lifetimeMs: 7 * 86_400_000, partBytes: 2 * 1024 ** 3, staleMs: 3_600_000, perDay: 3 } as const;

/**
 * A name as it can go in a file name or a folder: accents dropped, lower case, words joined by hyphens, forty characters
 * at most. The export's folders and the name a download is saved under both come from here, so they cannot disagree.
 */
export function fileSlug(text: string, fallback: string): string {
  return text.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
    .slice(0, 40).replace(/-+$/, "") || fallback;
}

/** `export_workspace`: one export, by id. The workspace travels too, so the job never reads a row that is not its own. */
export const ExportJob = z.object({ workspaceId: z.uuid(), exportId: z.uuid(), traceId: z.string().min(1) });
export type ExportJob = z.infer<typeof ExportJob>;

export const JobEnvelope = z.object({
  workspaceId: z.uuid(),
  projectId: z.uuid().optional(),
  shotId: z.uuid().optional(),
  callId: z.uuid().optional(),
  traceId: z.string().min(1),
  /**
   * Anything a queue wants to carry that is not in the envelope. Optional because it always was: `z.unknown()`
   * parses a missing key happily, but inferred as required it put a field on every job type that no sender sets
   * and no handler reads — invisible until something constructed a job as a literal rather than through `parse`.
   */
  payload: z.unknown().optional(),
});
export type JobEnvelope = z.infer<typeof JobEnvelope>;

/**
 * `judge_prospect`: read one company's own pages and score it against its workspace's table (23).
 *
 * `draftAs` is the person who brought the company on a list (23 §a list somebody brings): a company they imported to
 * write to is drafted for as soon as the table says *go*, in their name, by the same `draftFor` a press runs, and the
 * letter waits for the owner's yes like any other. Absent for a company a finder found, which waits for a press or
 * for the loop, as it always has.
 */
export const JudgeJob = z.object({
  workspaceId: z.uuid(), prospectId: z.uuid(), traceId: z.string().min(1), draftAs: z.uuid().optional(),
});
export type JudgeJob = z.infer<typeof JudgeJob>;

/** `send_letter`: one approved letter, out through the workspace's own mailbox (22 §sending). */
export const SendJob = z.object({ workspaceId: z.uuid(), letterId: z.uuid(), traceId: z.string().min(1) });
export type SendJob = z.infer<typeof SendJob>;
